import { Extension } from '@tiptap/react';
import { Plugin, Selection, TextSelection } from '@tiptap/pm/state';
import type { Node as PMNode } from '@tiptap/pm/model';

export const ShiftClickSelection = Extension.create({
  name: 'shiftClickSelection',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          handleDOMEvents: {
            mousedown(view, event) {
              if (!(event instanceof MouseEvent) || !event.shiftKey || event.detail !== 1) {
                return false;
              }

              const currentSelection = view.state.selection;
              if (!(currentSelection instanceof TextSelection)) {
                return false;
              }

              const posAtCoords = view.posAtCoords({
                left: event.clientX,
                top: event.clientY,
              });
              if (!posAtCoords) {
                return false;
              }

              const nextSelection = createShiftClickSelection({
                anchor: currentSelection.anchor,
                head: posAtCoords.pos,
                doc: view.state.doc,
              });
              if (!nextSelection) {
                return false;
              }

              event.preventDefault();
              view.dispatch(view.state.tr.setSelection(nextSelection).scrollIntoView());
              return true;
            },
          },
        },
      }),
    ];
  },
});

export const createShiftClickSelection = ({ anchor, head, doc }: { anchor: number; head: number; doc: PMNode }) => {
  const docSize = doc.content.size;
  const maxPos = Math.max(1, docSize - 1);
  const clamp = (pos: number) => Math.max(1, Math.min(maxPos, pos));
  const isInline = (pos: number) => {
    try {
      return doc.resolve(pos).parent.inlineContent;
    } catch {
      return false;
    }
  };
  const safeAnchor = clamp(anchor);
  const clampedHead = clamp(head);

  try {
    const selection = TextSelection.create(doc, safeAnchor, clampedHead);
    if (isInline(selection.anchor) && isInline(selection.head)) {
      return selection;
    }
  } catch {}

  const $head = doc.resolve(clampedHead);
  const direction: 1 | -1 = clampedHead >= safeAnchor ? 1 : -1;
  const preferred = Selection.findFrom($head, direction, true);
  const fallback = Selection.findFrom($head, direction === 1 ? -1 : 1, true);
  const safeHead = preferred instanceof TextSelection ? preferred.head : fallback instanceof TextSelection ? fallback.head : null;

  if (safeHead == null || !isInline(safeAnchor) || !isInline(safeHead)) {
    return null;
  }

  return TextSelection.create(doc, safeAnchor, safeHead);
};
